import React, { useEffect, useState } from 'react';
import { 
  Box,
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField,
  Typography,
  IconButton,
  Divider,
  Chip,
  useTheme,
  MenuItem,
  FormControl,
  Select,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import {
  CloseRounded,
  DescriptionRounded,
  PersonRounded,
  EventRounded,
  StarRounded,
  StarOutlineRounded,
  CategoryRounded,
  InfoRounded
} from '@mui/icons-material';

const EditTaskModal = ({ open, handleClose, handleSave, task }) => {
  const theme = useTheme();
  const [taskData, setTaskData] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTaskData(task || {});
      setIsSubmitting(false);
    }
  }, [open, task]);

  const handleChange = ({ target: { name, value } }) => {
    setTaskData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setTaskData(prev => ({ ...prev, dueDate: date }));
  };

  const handleSaveClick = async () => {
    setIsSubmitting(true);
    try {
      await handleSave(taskData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityIcon = (level) => {
    const isSelected = taskData.priority === level;
    const colorMap = {
      "Alta": theme.palette.error.main,
      "Média": theme.palette.warning.main,
      "Baixa": theme.palette.success.main
    };

    return (
      <Box display="flex" alignItems="center">
        {isSelected ? (
          <StarRounded sx={{ color: colorMap[level], mr: 1 }} />
        ) : (
          <StarOutlineRounded sx={{ color: theme.palette.text.disabled, mr: 1 }} />
        )}
        <Typography variant="body2">{level}</Typography>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={isSubmitting ? null : handleClose}
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: theme.palette.background.paper,
          boxShadow: theme.shadows[24],
          overflow: "hidden",
          border: `1px solid ${theme.palette.divider}`,
          transform: open ? 'scale(1)' : 'scale(0.95)',
          transition: 'transform 0.2s ease-in-out'
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0,0,0,0.2)'
        }
      }}
    >
      {/* Premium Header with floating effect */}
      <DialogTitle sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
        padding: "20px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: theme.shadows[2]
      }}>
        <Typography variant="h6" sx={{ 
          fontSize: "1.25rem", 
          letterSpacing: "0.5px",
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DescriptionRounded fontSize="small" />
          Editar Tarefa
        </Typography>
        <IconButton 
          onClick={handleClose} 
          sx={{ 
            color: "inherit",
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
          disabled={isSubmitting}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column", 
          gap: 3,
          '& > *': {
            transition: 'all 0.2s ease'
          }
        }}>
          {/* Title Field with floating label effect */}
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="subtitle2" sx={{ 
                color: focusedField === 'title' ? theme.palette.primary.main : theme.palette.text.secondary,
                fontWeight: 600
              }}>
                Título da Tarefa *
              </Typography>
              <Tooltip title="Título claro ajuda na organização" arrow>
                <InfoRounded color="disabled" sx={{ fontSize: 16, ml: 1 }} />
              </Tooltip>
            </Box>
            <TextField
              name="title"
              value={taskData.title || ''}
              onChange={handleChange}
              onFocus={() => setFocusedField('title')}
              onBlur={() => setFocusedField(null)}
              required
              variant="outlined"
              fullWidth
              placeholder="Edite o título da tarefa"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: focusedField === 'title' ? theme.palette.primary.main : theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.light
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionRounded sx={{ 
                      color: focusedField === 'title' ? theme.palette.primary.main : theme.palette.text.disabled,
                      mr: 1
                    }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Description Field with animated expansion */}
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="subtitle2" sx={{ 
                color: focusedField === 'description' ? theme.palette.primary.main : theme.palette.text.secondary,
                fontWeight: 600
              }}>
                Descrição *
              </Typography>
              <Tooltip title="Detalhes ajudam na execução" arrow>
                <InfoRounded color="disabled" sx={{ fontSize: 16, ml: 1 }} />
              </Tooltip>
            </Box>
            <TextField
              name="description"
              value={taskData.description || ''}
              onChange={handleChange}
              onFocus={() => setFocusedField('description')}
              onBlur={() => setFocusedField(null)}
              multiline
              minRows={3}
              maxRows={8}
              required
              variant="outlined"
              fullWidth
              placeholder="Edite os detalhes da tarefa..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  transition: 'all 0.3s ease',
                  "& fieldset": {
                    borderColor: focusedField === 'description' ? theme.palette.primary.main : theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.light
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
                  }
                }
              }}
            />
          </Box>

          <Divider sx={{ 
            my: 1, 
            borderColor: theme.palette.divider,
            borderBottomWidth: '1px',
            opacity: 0.8
          }} />

          {/* Client Field with avatar */}
          <Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="subtitle2" sx={{ 
                color: focusedField === 'client' ? theme.palette.primary.main : theme.palette.text.secondary,
                fontWeight: 600
              }}>
                Cliente *
              </Typography>
              <Tooltip title="Identifique para quem é a tarefa" arrow>
                <InfoRounded color="disabled" sx={{ fontSize: 16, ml: 1 }} />
              </Tooltip>
            </Box>
            <TextField
              name="client"
              value={taskData.client || ''}
              onChange={handleChange}
              onFocus={() => setFocusedField('client')}
              onBlur={() => setFocusedField(null)}
              required
              variant="outlined"
              fullWidth
              placeholder="Edite o nome do cliente"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: focusedField === 'client' ? theme.palette.primary.main : theme.palette.divider,
                    borderWidth: "2px"
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.light
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonRounded sx={{ 
                      color: focusedField === 'client' ? theme.palette.primary.main : theme.palette.text.disabled,
                      mr: 1
                    }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {/* Priority Field with interactive stars */}
          {taskData.priority && (
            <Box>
              <Typography variant="subtitle2" sx={{ 
                mb: 1, 
                color: theme.palette.text.secondary,
                fontWeight: 600
              }}>
                Prioridade
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="priority"
                  value={taskData.priority || 'Média'}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{
                    borderRadius: "10px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                      borderWidth: "2px"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.light
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "10px",
                        marginTop: "8px",
                        boxShadow: theme.shadows[8],
                        border: `1px solid ${theme.palette.divider}`
                      }
                    }
                  }}
                >
                  <MenuItem value="Baixa" sx={{ py: 1.5 }}>
                    {getPriorityIcon("Baixa")}
                  </MenuItem>
                  <MenuItem value="Média" sx={{ py: 1.5 }}>
                    {getPriorityIcon("Média")}
                  </MenuItem>
                  <MenuItem value="Alta" sx={{ py: 1.5 }}>
                    {getPriorityIcon("Alta")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Date Field with calendar icon */}
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              color: theme.palette.text.secondary,
              fontWeight: 600
            }}>
              Data de Vencimento
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
              <DatePicker
                value={taskData.dueDate ? new Date(taskData.dueDate) : new Date()}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        "& fieldset": {
                          borderColor: theme.palette.divider,
                          borderWidth: "2px"
                        },
                        "&:hover fieldset": {
                          borderColor: theme.palette.primary.light
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: theme.palette.primary.main,
                          boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
                        }
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <EventRounded sx={{ 
                            color: theme.palette.text.disabled,
                            mr: 1
                          }} />
                        </InputAdornment>
                      )
                    }}
                  />
                )}
                inputFormat="dd/MM/yyyy"
              />
            </LocalizationProvider>
          </Box>

          {/* Category Field with visual chips */}
          {taskData.category && (
            <Box>
              <Typography variant="subtitle2" sx={{ 
                mb: 1, 
                color: theme.palette.text.secondary,
                fontWeight: 600
              }}>
                Categoria
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="category"
                  value={taskData.category || ''}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{
                    borderRadius: "10px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                      borderWidth: "2px"
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.light
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "10px",
                        marginTop: "8px",
                        boxShadow: theme.shadows[8],
                        border: `1px solid ${theme.palette.divider}`
                      }
                    }
                  }}
                >
                  <MenuItem value="Documentação" sx={{ py: 1.5 }}>
                    <Chip 
                      label="Documentação" 
                      size="small" 
                      sx={{ mr: 1 }} 
                      icon={<DescriptionRounded fontSize="small" />}
                    />
                  </MenuItem>
                  <MenuItem value="Visto" sx={{ py: 1.5 }}>
                    <Chip 
                      label="Visto" 
                      size="small" 
                      sx={{ mr: 1 }} 
                      color="info"
                      icon={<CategoryRounded fontSize="small" />}
                    />
                  </MenuItem>
                  <MenuItem value="Financeiro" sx={{ py: 1.5 }}>
                    <Chip 
                      label="Financeiro" 
                      size="small" 
                      sx={{ mr: 1 }} 
                      color="success"
                      icon={<CategoryRounded fontSize="small" />}
                    />
                  </MenuItem>
                  <MenuItem value="Logística" sx={{ py: 1.5 }}>
                    <Chip 
                      label="Logística" 
                      size="small" 
                      sx={{ mr: 1 }} 
                      color="warning"
                      icon={<CategoryRounded fontSize="small" />}
                    />
                  </MenuItem>
                  <MenuItem value="Outro" sx={{ py: 1.5 }}>
                    <Chip 
                      label="Outro" 
                      size="small" 
                      sx={{ mr: 1 }} 
                      color="default"
                      icon={<CategoryRounded fontSize="small" />}
                    />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Premium Footer Actions with loading state */}
      <DialogActions sx={{ 
        padding: "16px 24px",
        borderTop: `1px solid ${theme.palette.divider}`,
        justifyContent: "space-between",
        background: theme.palette.background.default
      }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          disabled={isSubmitting}
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 600,
            borderRadius: "10px",
            padding: "10px 20px",
            borderColor: theme.palette.divider,
            "&:hover": {
              borderColor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            },
            minWidth: "120px",
            transition: 'all 0.2s ease'
          }}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSaveClick} 
          variant="contained"
          disabled={!taskData.title || !taskData.description || !taskData.client || isSubmitting}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.darker} 100%)`,
              boxShadow: theme.shadows[4],
              transform: 'translateY(-1px)'
            },
            "&:active": {
              transform: 'translateY(0)'
            },
            "&.Mui-disabled": {
              background: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled
            },
            fontWeight: 700,
            borderRadius: "10px",
            padding: "10px 24px",
            boxShadow: theme.shadows[2],
            minWidth: "160px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}
        >
          {isSubmitting ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{
                width: 16,
                height: 16,
                border: `2px solid ${theme.palette.background.paper}`,
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
              Salvando...
            </Box>
          ) : (
            'Salvar Alterações'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTaskModal;